import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TypeAheadSelect } from '@/components/ui/type-ahead-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';
import { questionApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { KatexPreviewPanel } from '@/components/ui/katex-preview';
import { ImageUpload } from '@/components/ui/image-upload';

interface Option {
  text: string;
  isCorrect: boolean;
  imageUrl?: string;
}

interface AddQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Supported question types only
const QUESTION_TYPES = [
  { value: 'MCQ_SINGLE_CORRECT', label: 'MCQ Single Correct' },
  { value: 'MCQ_MULTIPLE_CORRECT', label: 'MCQ Multiple Correct' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'FILL_IN_THE_BLANK_NUMERIC', label: 'Fill in the Blank (Numeric)' },
  { value: 'ESSAY', label: 'Essay' },
  { value: 'MATCHING', label: 'Matching' },
  { value: 'READING_COMPREHENSION', label: 'Reading Comprehension' },
];

const DIFFICULTY_LEVELS = [
  { value: 'EASY', label: 'Easy' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HARD', label: 'Hard' }
];

const CLASS_LEVELS = [
  { value: '8', label: 'Class 8' },
  { value: '9', label: 'Class 9' },
  { value: '10', label: 'Class 10' },
  { value: '11', label: 'Class 11' },
  { value: '12', label: 'Class 12' },
];

export const AddQuestionDialog: React.FC<AddQuestionDialogProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    questionText: '',
    questionImageUrl: '',
    type: 'MCQ_SINGLE_CORRECT',
    options: [
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' }
    ] as Option[],
    numericalAnswer: '',
    essayPrompt: '', // For essay questions
    essayMaxWords: 500, // Maximum word count for essays
    matchingPairs: [ // For matching questions
      { left: '', right: '', id: 1 },
      { left: '', right: '', id: 2 }
    ],
    explanation: '',
    explanationImageUrl: '',
    reference: '',
    subject: '',
    topic: '',
    subtopic: '',
    difficultyLevel: 'MEDIUM',
    classLevel: '',
    passage: '',
    passageId: '',
    examTypes: [] as string[], // NEW: Multiple exam types support
    tags: [] as string[], // NEW: Tag-based categorization
    tenantCode: ''
  });

  // Type-ahead options
  const [subjectOptions, setSubjectOptions] = useState<Array<{value: string, label: string}>>([]);
  const [topicOptions, setTopicOptions] = useState<Array<{value: string, label: string}>>([]);
  const [subtopicOptions, setSubtopicOptions] = useState<Array<{value: string, label: string}>>([]);
  const [examTypeOptions, setExamTypeOptions] = useState<Array<{value: string, label: string}>>([]);

  // Loading states
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false);
  const [subtopicLoading, setSubtopicLoading] = useState(false);
  const [examTypeLoading, setExamTypeLoading] = useState(false);

  // Tag management state
  const [newTag, setNewTag] = useState('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadInitialData();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      questionText: '',
      questionImageUrl: '',
      type: 'MCQ_SINGLE_CORRECT',
      options: [
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' }
      ],
      numericalAnswer: '',
      essayPrompt: '',
      essayMaxWords: 500,
      matchingPairs: [
        { left: '', right: '', id: 1 },
        { left: '', right: '', id: 2 }
      ],
      explanation: '',
      explanationImageUrl: '',
      reference: '',
      subject: '',
      topic: '',
      subtopic: '',
      difficultyLevel: 'MEDIUM',
      classLevel: '',
      passage: '',
      passageId: '',
      examTypes: [], // NEW: Multiple exam types support
      tags: [], // NEW: Tag-based categorization
      tenantCode: ''
    });
  };

  const loadInitialData = async () => {
    try {
      // Load initial subjects and exam types
      await Promise.all([
        searchSubjects(''),
        searchExamTypes('')
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // Search functions
  const searchSubjects = async (search: string) => {
    try {
      setSubjectLoading(true);
      const results = await questionApi.searchSubjects(search);
      setSubjectOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching subjects:', error);
    } finally {
      setSubjectLoading(false);
    }
  };

  const searchTopics = async (search: string) => {
    try {
      setTopicLoading(true);
      const results = await questionApi.searchTopics(formData.subject, search);
      setTopicOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching topics:', error);
    } finally {
      setTopicLoading(false);
    }
  };

  const searchSubtopics = async (search: string) => {
    try {
      setSubtopicLoading(true);
      const results = await questionApi.searchSubtopics(formData.topic, search);
      setSubtopicOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching subtopics:', error);
    } finally {
      setSubtopicLoading(false);
    }
  };

  const searchExamTypes = async (_search: string) => {
    try {
      setExamTypeLoading(true);
      const results = await questionApi.getExamTypes();
      setExamTypeOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching exam types:', error);
    } finally {
      setExamTypeLoading(false);
    }
  };

  // Tag management functions
  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle subject change - reset topic and subtopic
  const handleSubjectChange = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      subject,
      topic: '',
      subtopic: ''
    }));
    setTopicOptions([]);
    setSubtopicOptions([]);
    if (subject) {
      searchTopics('');
    }
  };

  // Handle topic change - reset subtopic
  const handleTopicChange = (topic: string) => {
    setFormData(prev => ({
      ...prev,
      topic,
      subtopic: ''
    }));
    setSubtopicOptions([]);
    if (topic) {
      searchSubtopics('');
    }
  };

  // Handle option changes
  const updateOption = (index: number, text: string) => {
    const newOptions = [...formData.options];
    newOptions[index].text = text;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const toggleOptionCorrect = (index: number) => {
    const newOptions = [...formData.options];

    if (formData.type === 'MCQ_SINGLE_CORRECT') {
      // For single correct, uncheck all others
      newOptions.forEach((option, i) => {
        option.isCorrect = i === index;
      });
    } else {
      // For multiple correct, toggle this option
      newOptions[index].isCorrect = !newOptions[index].isCorrect;
    }

    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { text: '', isCorrect: false, imageUrl: '' }]
      }));
    }
  };

  const removeOption = (index: number) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const updateOptionImage = (index: number, imageUrl: string) => {
    const newOptions = [...formData.options];
    newOptions[index].imageUrl = imageUrl;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  // Matching pairs functions
  const addMatchingPair = () => {
    const newId = Math.max(...formData.matchingPairs.map(p => p.id)) + 1;
    setFormData(prev => ({
      ...prev,
      matchingPairs: [...prev.matchingPairs, { left: '', right: '', id: newId }]
    }));
  };

  const removeMatchingPair = (id: number) => {
    if (formData.matchingPairs.length > 2) {
      setFormData(prev => ({
        ...prev,
        matchingPairs: prev.matchingPairs.filter(pair => pair.id !== id)
      }));
    }
  };

  const updateMatchingPair = (id: number, field: 'left' | 'right', value: string) => {
    setFormData(prev => ({
      ...prev,
      matchingPairs: prev.matchingPairs.map(pair =>
        pair.id === id ? { ...pair, [field]: value } : pair
      )
    }));
  };

  // Handle question type change
  const handleQuestionTypeChange = (type: string) => {
    setFormData(prev => {
      const newOptions = [...prev.options];

      if (type === 'TRUE_FALSE') {
        // For True/False, set exactly 2 options
        return {
          ...prev,
          type,
          options: [
            { text: 'True', isCorrect: false },
            { text: 'False', isCorrect: false }
          ]
        };
      } else if (type === 'MCQ_SINGLE_CORRECT' || type === 'MULTIPLE_CHOICE') {
        // For single correct, ensure only one is selected
        let hasCorrect = false;
        newOptions.forEach(option => {
          if (option.isCorrect && !hasCorrect) {
            hasCorrect = true;
          } else {
            option.isCorrect = false;
          }
        });
      } else if (type === 'ESSAY') {
        // For essay questions, clear options and set defaults
        return {
          ...prev,
          type,
          options: [],
          essayPrompt: prev.questionText || '',
          essayMaxWords: 500
        };
      } else if (type === 'MATCHING') {
        // For matching questions, initialize with default pairs
        return {
          ...prev,
          type,
          options: [],
          matchingPairs: [
            { left: '', right: '', id: 1 },
            { left: '', right: '', id: 2 },
            { left: '', right: '', id: 3 }
          ]
        };
      }

      return {
        ...prev,
        type,
        options: newOptions
      };
    });
  };

  // Validation
  const validateForm = () => {
    if (!formData.questionText.trim()) {
      toast({ title: "Error", description: "Question text is required", variant: "destructive" });
      return false;
    }

    if (!formData.subject.trim()) {
      toast({ title: "Error", description: "Subject is required", variant: "destructive" });
      return false;
    }

    if (!formData.topic.trim()) {
      toast({ title: "Error", description: "Topic is required", variant: "destructive" });
      return false;
    }

    if (formData.type.startsWith('MCQ') || formData.type === 'TRUE_FALSE' || formData.type === 'MULTIPLE_CHOICE') {
      const hasCorrectAnswer = formData.options.some(option => option.isCorrect);
      if (!hasCorrectAnswer) {
        toast({ title: "Error", description: "At least one correct answer must be selected", variant: "destructive" });
        return false;
      }

      const hasEmptyOption = formData.options.some(option => !option.text.trim());
      if (hasEmptyOption) {
        toast({ title: "Error", description: "All options must have text", variant: "destructive" });
        return false;
      }
    }

    if (formData.type === 'FILL_IN_THE_BLANK_NUMERIC' && !formData.numericalAnswer.trim()) {
      toast({ title: "Error", description: "Answer is required", variant: "destructive" });
      return false;
    }

    if (formData.type === 'ESSAY') {
      if (!formData.essayPrompt.trim()) {
        toast({ title: "Error", description: "Essay prompt is required", variant: "destructive" });
        return false;
      }
      if (formData.essayMaxWords <= 0) {
        toast({ title: "Error", description: "Maximum word count must be greater than 0", variant: "destructive" });
        return false;
      }
    }

    if (formData.type === 'MATCHING') {
      const hasEmptyPair = formData.matchingPairs.some(pair => !pair.left.trim() || !pair.right.trim());
      if (hasEmptyPair) {
        toast({ title: "Error", description: "All matching pairs must have both left and right values", variant: "destructive" });
        return false;
      }
      if (formData.matchingPairs.length < 2) {
        toast({ title: "Error", description: "At least 2 matching pairs are required", variant: "destructive" });
        return false;
      }
    }

    if (!formData.tags || formData.tags.length === 0) {
      toast({ title: "Error", description: "At least one tag is required to categorize the question", variant: "destructive" });
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const questionData = {
        questionText: formData.questionText,
        questionImageUrl: formData.questionImageUrl || null,
        type: formData.type,
        options: (formData.type === 'FILL_IN_THE_BLANK_NUMERIC' || formData.type === 'ESSAY' || formData.type === 'MATCHING')
          ? []
          : formData.options.map((opt, index) => ({
              text: opt.text,
              isCorrect: opt.isCorrect,
              imageUrl: opt.imageUrl || null,
              optionLetter: String.fromCharCode(65 + index),
              optionIndex: index + 1
            })),
        correctAnswers: formData.type === 'FILL_IN_THE_BLANK_NUMERIC'
          ? [formData.numericalAnswer]
          : formData.type === 'ESSAY'
          ? [] // Essays don't have predefined correct answers
          : formData.type === 'MATCHING'
          ? formData.matchingPairs.map(pair => `${pair.left}:${pair.right}`)
          : formData.options.filter(opt => opt.isCorrect).map(opt => opt.text),
        // Additional fields for specific question types
        essayPrompt: formData.type === 'ESSAY' ? formData.essayPrompt : null,
        essayMaxWords: formData.type === 'ESSAY' ? formData.essayMaxWords : null,
        matchingPairs: formData.type === 'MATCHING' ? formData.matchingPairs : null,
        explanation: formData.explanation || null,
        explanationImageUrl: formData.explanationImageUrl || null,
        reference: formData.reference || null,
        classLevel: formData.classLevel || null,
        passage: formData.passage || null,
        passageId: formData.passageId || null,
        subject: formData.subject,
        topic: formData.topic,
        subtopic: formData.subtopic || null,
        difficultyLevel: formData.difficultyLevel,
        examTypes: formData.examTypes, // NEW: Multiple exam types support
        tags: formData.tags, // NEW: Tag-based categorization
        tenantCode: formData.tenantCode || null
      };

      await questionApi.create(questionData);

      toast({
        title: "Success",
        description: "Question created successfully"
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating question:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create question",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Question</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-6">
          {/* Question Type and Difficulty - Moved to top for immediate form changes */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-blue-800 font-medium">Question Type *</Label>
                <Select value={formData.type} onValueChange={handleQuestionTypeChange}>
                  <SelectTrigger className="border-blue-300 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-blue-800 font-medium">Difficulty *</Label>
                <Select value={formData.difficultyLevel} onValueChange={(value) => setFormData(prev => ({ ...prev, difficultyLevel: value }))}>
                  <SelectTrigger className="border-blue-300 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTY_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-blue-600">
              Select the question type first - this will change the form fields below
            </p>
          </div>

          {/* Passage fields for Reading Comprehension */}
          {formData.type === 'READING_COMPREHENSION' && (
            <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="space-y-2">
                <Label className="text-amber-800 font-medium">Passage ID *</Label>
                <Input
                  value={formData.passageId}
                  onChange={(e) => setFormData(prev => ({ ...prev, passageId: e.target.value }))}
                  placeholder="Unique ID to group sub-questions (e.g., PASSAGE_001)"
                />
                <p className="text-xs text-amber-700">
                  All sub-questions sharing this ID will be grouped under the same passage
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-amber-800 font-medium">Passage Text</Label>
                <Textarea
                  value={formData.passage}
                  onChange={(e) => setFormData(prev => ({ ...prev, passage: e.target.value }))}
                  placeholder="Enter the passage text (shared across all sub-questions with the same Passage ID)"
                  rows={5}
                />
                <p className="text-xs text-amber-700">
                  The passage only needs to be entered on one sub-question; others with the same Passage ID will share it
                </p>
              </div>
            </div>
          )}

          {/* Subject, Topic, and Subtopic */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <TypeAheadSelect
                value={formData.subject}
                onValueChange={handleSubjectChange}
                placeholder="Select or add subject..."
                options={subjectOptions}
                onSearch={searchSubjects}
                loading={subjectLoading}
                allowCreate={true}
                onCreateNew={(value) => handleSubjectChange(value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Topic *</Label>
              <TypeAheadSelect
                value={formData.topic}
                onValueChange={handleTopicChange}
                placeholder="Select or add topic..."
                options={topicOptions}
                onSearch={searchTopics}
                loading={topicLoading}
                allowCreate={true}
                onCreateNew={(value) => handleTopicChange(value)}
                disabled={!formData.subject}
              />
            </div>

            <div className="space-y-2">
              <Label>Subtopic</Label>
              <TypeAheadSelect
                value={formData.subtopic}
                onValueChange={(value) => setFormData(prev => ({ ...prev, subtopic: value }))}
                placeholder="Select or add subtopic..."
                options={subtopicOptions}
                onSearch={searchSubtopics}
                loading={subtopicLoading}
                allowCreate={true}
                onCreateNew={(value) => setFormData(prev => ({ ...prev, subtopic: value }))}
                disabled={!formData.topic}
              />
            </div>
          </div>

          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="questionText">Question Text *</Label>
            <Textarea
              id="questionText"
              value={formData.questionText}
              onChange={(e) => setFormData(prev => ({ ...prev, questionText: e.target.value }))}
              placeholder="Enter the question text (LaTeX supported)..."
              rows={3}
            />
          </div>

          {/* Question Image */}
          <ImageUpload
            label="Question Image (Optional)"
            value={formData.questionImageUrl}
            onChange={(imageUrl) => setFormData(prev => ({ ...prev, questionImageUrl: imageUrl || '' }))}
            entityId={`question-${Date.now()}`}
            imageType="QUESTION"
          />



          {/* Options for MCQ and True/False */}
          {(formData.type.startsWith('MCQ') || formData.type === 'TRUE_FALSE' || formData.type === 'MULTIPLE_CHOICE') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Answer Options *</Label>
                {formData.type.startsWith('MCQ') && formData.options.length < 6 && (
                  <Button type="button" variant="outline" size="sm" onClick={addOption}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Option
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {formData.options.map((option, index) => (
                  <div key={index} className="space-y-2 p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={option.isCorrect}
                        onCheckedChange={() => toggleOptionCorrect(index)}
                      />
                      <Input
                        value={option.text}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + index)} text`}
                        className="flex-1"
                      />
                      {formData.type.startsWith('MCQ') && formData.options.length > 2 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeOption(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {/* Option Image Upload */}
                    <ImageUpload
                      label={`Option ${String.fromCharCode(65 + index)} Image (Optional)`}
                      value={option.imageUrl}
                      onChange={(imageUrl) => updateOptionImage(index, imageUrl || '')}
                      entityId={`option-${index}-${Date.now()}`}
                      imageType="OPTION"
                      className="ml-6"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {formData.type === 'MCQ_SINGLE_CORRECT'
                  ? 'Select exactly one correct answer'
                  : formData.type === 'MCQ_MULTIPLE_CORRECT'
                  ? 'Select one or more correct answers'
                  : 'Select the correct answer (True or False)'
                }
              </p>
            </div>
          )}

          {/* Fill in the Blank Numeric Answer */}
          {formData.type === 'FILL_IN_THE_BLANK_NUMERIC' && (
            <div className="space-y-2">
              <Label htmlFor="numericalAnswer">Numerical Answer *</Label>
              <Input
                id="numericalAnswer"
                type="number"
                step="any"
                value={formData.numericalAnswer}
                onChange={(e) => setFormData(prev => ({ ...prev, numericalAnswer: e.target.value }))}
                placeholder="Enter the numerical answer"
              />
            </div>
          )}

          {/* Essay Question Fields */}
          {formData.type === 'ESSAY' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="essayPrompt">Essay Prompt *</Label>
                <Textarea
                  id="essayPrompt"
                  value={formData.essayPrompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, essayPrompt: e.target.value }))}
                  placeholder="Enter the essay prompt or question..."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  This will be the main question/prompt that students need to respond to in essay format.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="essayMaxWords">Maximum Word Count *</Label>
                <Input
                  id="essayMaxWords"
                  type="number"
                  min="50"
                  max="2000"
                  value={formData.essayMaxWords}
                  onChange={(e) => setFormData(prev => ({ ...prev, essayMaxWords: parseInt(e.target.value) || 500 }))}
                  placeholder="500"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of words students can write (50-2000 words).
                </p>
              </div>
            </div>
          )}

          {/* Matching Question Fields */}
          {formData.type === 'MATCHING' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Matching Pairs *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addMatchingPair}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Pair
                </Button>
              </div>

              <div className="space-y-3">
                {formData.matchingPairs.map((pair) => (
                  <div key={pair.id} className="grid grid-cols-2 gap-4 p-3 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-sm">Left Side</Label>
                      <Input
                        value={pair.left}
                        onChange={(e) => updateMatchingPair(pair.id, 'left', e.target.value)}
                        placeholder="Enter left side text..."
                      />
                    </div>
                    <div className="space-y-1 flex items-end">
                      <div className="flex-1">
                        <Label className="text-sm">Right Side</Label>
                        <Input
                          value={pair.right}
                          onChange={(e) => updateMatchingPair(pair.id, 'right', e.target.value)}
                          placeholder="Enter right side text..."
                        />
                      </div>
                      {formData.matchingPairs.length > 2 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeMatchingPair(pair.id)}
                          className="ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                Students will need to match items from the left column with items from the right column.
              </p>
            </div>
          )}

          {/* Kit Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Exam Types - Multiple Selection */}
            <div className="space-y-2">
              <Label>Exam Types</Label>
              <p className="text-sm text-muted-foreground">
                Select exam types this question is suitable for (multiple selection allowed).
              </p>
              <TypeAheadSelect
                value=""
                onValueChange={(value) => {
                  if (value && !formData.examTypes.includes(value)) {
                    setFormData(prev => ({
                      ...prev,
                      examTypes: [...prev.examTypes, value]
                    }));
                  }
                }}
                placeholder="Select or add exam type..."
                options={examTypeOptions}
                onSearch={searchExamTypes}
                loading={examTypeLoading}
                allowCreate={true}
                onCreateNew={(value) => {
                  if (value && !formData.examTypes.includes(value)) {
                    setFormData(prev => ({
                      ...prev,
                      examTypes: [...prev.examTypes, value]
                    }));
                  }
                }}
              />

              {/* Selected Exam Types */}
              <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-lg bg-muted/50">
                {formData.examTypes.length > 0 ? (
                  formData.examTypes.map((examType, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                    >
                      {examType}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            examTypes: prev.examTypes.filter(et => et !== examType)
                          }));
                        }}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">No exam types selected</span>
                )}
              </div>
            </div>

            {/* Tags - Primary Categorization Method */}
            <div className="space-y-2">
              <Label>Tags *</Label>
              <p className="text-sm text-muted-foreground">
                Add tags to categorize this question. Tags determine which kits this question belongs to.
              </p>

              {/* Current Tags */}
              <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-lg bg-muted/50">
                {formData.tags.length > 0 ? (
                  formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">No tags added yet</span>
                )}
              </div>

              {/* Add Tag Input */}
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter a tag (e.g., mathematics, algebra, fundamentals)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={addTag}
                  disabled={!newTag.trim()}
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Suggested Tags */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Suggested tags:</p>
                <div className="flex flex-wrap gap-1">
                  {[
                    formData.subject?.toLowerCase(),
                    formData.topic?.toLowerCase(),
                    formData.difficultyLevel?.toLowerCase(),
                    'practice',
                    '2024'
                  ].filter(Boolean).filter(tag => !formData.tags.includes(tag!)).map((tag, index) => (
                    <Button
                      key={index}
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (tag && !formData.tags.includes(tag)) {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...prev.tags, tag]
                          }));
                        }
                      }}
                      className="h-6 px-2 text-xs"
                    >
                      + {tag}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tenant Access */}
          <div className="space-y-2">
            <Label>Access Scope</Label>
            <Input value="Current admin tenant only" disabled />
            <p className="text-xs text-muted-foreground">
              Questions created from the admin portal stay available only to your tenant. Shared questions for all admins are created from the super-admin portal.
            </p>
          </div>

          {/* Reference */}
          <div className="space-y-2">
            <Label htmlFor="reference">Reference (Optional)</Label>
            <Input
              id="reference"
              value={formData.reference}
              onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="Book, article, chapter reference"
            />
            <p className="text-xs text-muted-foreground">
              Source reference where this question is inspired from
            </p>
          </div>

          {/* Class Level */}
          <div className="space-y-2">
            <Label htmlFor="classLevel">Class (Optional)</Label>
            <Select value={formData.classLevel} onValueChange={(value) => setFormData(prev => ({ ...prev, classLevel: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent>
                {CLASS_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <Label htmlFor="explanation">Explanation (Optional)</Label>
            <Textarea
              id="explanation"
              value={formData.explanation}
              onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder="Enter explanation for the answer (LaTeX supported)..."
              rows={3}
            />
          </div>

          {/* Explanation Image */}
          <ImageUpload
            label="Explanation Image (Optional)"
            value={formData.explanationImageUrl}
            onChange={(imageUrl) => setFormData(prev => ({ ...prev, explanationImageUrl: imageUrl || '' }))}
            entityId={`explanation-${Date.now()}`}
            imageType="EXPLANATION"
          />

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Creating...' : 'Create Question'}
            </Button>
          </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Live Preview</h3>
            <KatexPreviewPanel
              questionText={formData.questionText}
              questionType={formData.type}
              options={(formData.type === 'FILL_IN_THE_BLANK_NUMERIC' || formData.type === 'ESSAY' || formData.type === 'MATCHING') ? [] : formData.options}
              numericalAnswer={formData.type === 'FILL_IN_THE_BLANK_NUMERIC' ? formData.numericalAnswer : undefined}
              essayPrompt={formData.type === 'ESSAY' ? formData.essayPrompt : undefined}
              essayMaxWords={formData.type === 'ESSAY' ? formData.essayMaxWords : undefined}
              matchingPairs={formData.type === 'MATCHING' ? formData.matchingPairs : undefined}
              explanation={formData.explanation}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
